export const TIME_UNIT = {
  MINUTES: "minutes",
  HOURS: "hours",
  DAYS: "days",
} as const

export type TimeUnit = (typeof TIME_UNIT)[keyof typeof TIME_UNIT]

export const SCHEDULE_TYPE = {
  INTERVAL: "interval",
  TIME_OF_DAY: "time_of_day",
} as const

export type ScheduleType = (typeof SCHEDULE_TYPE)[keyof typeof SCHEDULE_TYPE]

export const RECURRENCE_TYPE = {
  DAILY: "daily",
  WEEKLY: "weekly",
} as const

export type RecurrenceType =
  (typeof RECURRENCE_TYPE)[keyof typeof RECURRENCE_TYPE]

export const SCHEDULE_AM_PM = {
  AM: "AM",
  PM: "PM",
} as const

export type ScheduleAmPm = (typeof SCHEDULE_AM_PM)[keyof typeof SCHEDULE_AM_PM]

interface SchedulePresetOption {
  label: string
  value: number
  unit: TimeUnit
}

const SCHEDULE_DAY_NAMES = [
  "SUN",
  "MON",
  "TUE",
  "WED",
  "THU",
  "FRI",
  "SAT",
] as const

export const DAY_NAME_TO_INDEX: Record<
  (typeof SCHEDULE_DAY_NAMES)[number],
  number
> = SCHEDULE_DAY_NAMES.reduce(
  (acc, dayName, index) => {
    acc[dayName] = index
    return acc
  },
  {} as Record<(typeof SCHEDULE_DAY_NAMES)[number], number>,
)

export const TIME_UNIT_LABELS: Record<
  TimeUnit,
  { singular: string; plural: string }
> = {
  [TIME_UNIT.MINUTES]: { singular: "minute", plural: "minutes" },
  [TIME_UNIT.HOURS]: { singular: "hour", plural: "hours" },
  [TIME_UNIT.DAYS]: { singular: "day", plural: "days" },
}

export const SCHEDULE_PRESET_OPTIONS: SchedulePresetOption[] = [
  { label: "15 min", value: 15, unit: TIME_UNIT.MINUTES },
  { label: "30 min", value: 30, unit: TIME_UNIT.MINUTES },
  { label: "1 hour", value: 1, unit: TIME_UNIT.HOURS },
  { label: "6 hours", value: 6, unit: TIME_UNIT.HOURS },
  { label: "12 hours", value: 12, unit: TIME_UNIT.HOURS },
  { label: "Daily", value: 1, unit: TIME_UNIT.DAYS },
  { label: "Weekly", value: 7, unit: TIME_UNIT.DAYS },
  { label: "Monthly", value: 30, unit: TIME_UNIT.DAYS },
]

export const DEFAULT_SELECTED_DAYS = [1, 2, 3, 4, 5] as const

export const DEFAULT_SCHEDULE_STATE = {
  scheduleType: SCHEDULE_TYPE.INTERVAL,
  selectedPreset: 0,
  customValue: "1",
  customUnit: TIME_UNIT.HOURS,
  scheduleHour: "9",
  scheduleMinute: "00",
  scheduleAmPm: SCHEDULE_AM_PM.AM,
  recurrenceType: RECURRENCE_TYPE.DAILY,
} as const satisfies {
  scheduleType: ScheduleType
  selectedPreset: number
  customValue: string
  customUnit: TimeUnit
  scheduleHour: string
  scheduleMinute: string
  scheduleAmPm: ScheduleAmPm
  recurrenceType: RecurrenceType
}

export function buildRateExpression(value: number, unit: TimeUnit): string {
  const rateDisplay = formatRateDisplay(value, unit)
  return `rate(${rateDisplay})`
}

export function formatRateDisplay(value: number, unit: TimeUnit): string {
  const unitLabel = TIME_UNIT_LABELS[unit]
  return `${value} ${value === 1 ? unitLabel.singular : unitLabel.plural}`
}

export function buildCronExpression(
  hour: number,
  minute: number,
  recurrenceType: RecurrenceType,
  selectedDays: Set<number>,
): string {
  if (recurrenceType === RECURRENCE_TYPE.DAILY) {
    return `cron(${minute} ${hour} * * ? *)`
  }
  if (selectedDays.size === 0) {
    throw new Error("selectedDays must not be empty for WEEKLY recurrence")
  }
  const daysList = Array.from(selectedDays)
    .sort()
    .map((day) => SCHEDULE_DAY_NAMES[day])
    .join(",")
  return `cron(${minute} ${hour} ? * ${daysList} *)`
}
